import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, tokens } from '../design-system';
import { useSettings } from '../contexts/SettingsContext';
import apiClient from '../services/unifiedApiClient';
import JiraExportProgress from './JiraExportProgress';
import TrelloExportProgress from './TrelloExportProgress';

// Derive a JIRA-safe project key from a plan name
const deriveProjectKey = (name) => {
  const key = (name || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .substring(0, 8);
  return key || 'PLAN';
};

const RoadmapPushModal = ({ plan, isOpen, onClose, onSuccess }) => {
  const { settings } = useSettings();
  const navigate = useNavigate();

  // ── Phase: 'choose' | 'jira' | 'trello'
  const [phase, setPhase] = useState('choose');

  // ── Plan data (features + stories fetched from backend)
  const [planData, setPlanData] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  // ── Jira config fields
  const [jiraProjectName, setJiraProjectName] = useState('');
  const [jiraProjectKey, setJiraProjectKey] = useState('');

  // ── Trello config fields
  const [trelloBoardName, setTrelloBoardName] = useState('');

  // ── Export state
  const [isExporting, setIsExporting] = useState(false);
  const [showJiraProgress, setShowJiraProgress] = useState(false);
  const [showTrelloProgress, setShowTrelloProgress] = useState(false);
  const [jiraExportId, setJiraExportId] = useState(null);
  const [trelloExportId, setTrelloExportId] = useState(null);

  // ── Derived credential readiness
  const jiraInt = settings.jiraIntegration || {};
  const trelloInt = settings.trelloIntegration || {};

  const jiraReady =
    jiraInt.enabled &&
    jiraInt.jiraUrl &&
    jiraInt.userEmail &&
    jiraInt.apiToken;

  const trelloReady =
    trelloInt.enabled &&
    trelloInt.apiKey &&
    trelloInt.token;

  // ── Fetch plan data when modal opens
  useEffect(() => {
    if (!isOpen || !plan?.id) return;

    setPlanData(null);
    setPlanLoading(true);

    apiClient
      .get(`/plans/${plan.id}`)
      .then((res) => {
        // /plans/:id returns { data: { ... } } — unwrap the envelope
        const unwrapped = res?.data ?? res;
        console.log('[RoadmapPushModal] raw res keys:', Object.keys(res || {}));
        console.log('[RoadmapPushModal] unwrapped keys:', Object.keys(unwrapped || {}));
        console.log('[RoadmapPushModal] context keys:', Object.keys(unwrapped?.context || {}));
        console.log('[RoadmapPushModal] user_stories count:', unwrapped?.context?.user_stories?.length);
        console.log('[RoadmapPushModal] metadata keys:', Object.keys(unwrapped?.metadata || {}));
        setPlanData(unwrapped);
      })
      .catch((err) => {
        console.error('[RoadmapPushModal] Failed to fetch plan data:', err);
        setPlanData(null);
      })
      .finally(() => {
        setPlanLoading(false);
      });
  }, [isOpen, plan?.id]);

  // ── Pre-fill form fields from plan name whenever phase changes
  useEffect(() => {
    if (phase === 'jira') {
      setJiraProjectName(plan?.name || '');
      setJiraProjectKey(deriveProjectKey(plan?.name));
    } else if (phase === 'trello') {
      setTrelloBoardName(plan?.name || '');
    }
  }, [phase, plan?.name]);

  // ── Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPhase('choose');
      setIsExporting(false);
    }
  }, [isOpen]);

  // ── Build features+stories from plan data.
  // New plans: stories in context.user_stories with feature_name set — no context.features.
  // Legacy plans: features in context.features with nested stories.
  const features = useMemo(() => {
    if (!planData) return [];
    const stories = planData?.context?.user_stories || planData?.metadata?.user_stories || [];
    if (stories.some(s => s.feature_name)) {
      const map = new Map();
      stories.forEach(s => {
        const name = s.feature_name || 'General';
        if (!map.has(name)) {
          map.set(name, { id: name, title: name, description: '', priority: 3, scope_keywords: [], stories: [] });
        }
        map.get(name).stories.push(s);
      });
      return Array.from(map.values());
    }
    return planData?.context?.features || planData?.metadata?.features || [];
  }, [planData]);
  const storyCount = features.reduce((sum, f) => sum + (f.stories?.length || 0), 0);

  // ── Export helpers
  const buildFeaturesPayload = () =>
    features.map((f) => ({
      id: f.id,
      title: f.title,
      description: f.description || '',
      priority: f.priority || 3,
      scope_keywords: f.scope_keywords || [],
      stories: (f.stories || []).map((s) => ({
        id: s.id,
        actor: s.actor,
        action: s.action,
        benefit: s.benefit || '',
        acceptance_criteria: s.acceptance_criteria || [],
        priority: s.priority || 'should_have',
      })),
    }));

  const authHeader = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token') || 'dev-bypass-token'}`,
  });

  const handlePushToJira = async () => {
    if (!features.length) return;

    setIsExporting(true);
    onClose();
    setShowJiraProgress(true);

    try {
      const response = await fetch('/api/jira/start-export', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({
          config: {
            jira_url: jiraInt.jiraUrl,
            user_email: jiraInt.userEmail,
            api_token: jiraInt.apiToken,
            account_id: null,
            project_name: jiraProjectName,
            project_key: jiraProjectKey,
            users: [],
          },
          plan_id: plan.id,
          features: buildFeaturesPayload(),
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      setJiraExportId(result.export_id);
    } catch (err) {
      console.error('[RoadmapPushModal] Jira export start failed:', err);
      setShowJiraProgress(false);
      setJiraExportId(null);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePushToTrello = async () => {
    if (!features.length) return;

    setIsExporting(true);
    onClose();
    setShowTrelloProgress(true);

    try {
      const response = await fetch('/api/trello/start-export', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({
          config: {
            trello_api_key: trelloInt.apiKey,
            trello_token: trelloInt.token,
            board_name: trelloBoardName,
          },
          plan_id: plan.id,
          features: buildFeaturesPayload(),
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      setTrelloExportId(result.export_id);
    } catch (err) {
      console.error('[RoadmapPushModal] Trello export start failed:', err);
      setShowTrelloProgress(false);
      setTrelloExportId(null);
    } finally {
      setIsExporting(false);
    }
  };

  // ── Shared styles (mirrors ExportModal / ProgressModal conventions)
  const s = {
    label: {
      display: 'block',
      fontSize: tokens.typography.fontSize.sm[0],
      fontWeight: tokens.typography.fontWeight.medium,
      color: 'var(--color-text-muted)',
      marginBottom: tokens.spacing[1],
    },
    input: {
      width: '100%',
      padding: tokens.spacing[2],
      fontSize: tokens.typography.fontSize.sm[0],
      color: 'var(--color-text)',
      backgroundColor: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: tokens.borderRadius.md,
      outline: 'none',
      fontFamily: tokens.typography.fontFamily.sans.join(', '),
      boxSizing: 'border-box',
    },
    hint: {
      fontSize: tokens.typography.fontSize.xs[0],
      color: 'var(--color-text-muted)',
      marginTop: tokens.spacing[1],
    },
    grid2: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: tokens.spacing[3],
    },
    infoBox: {
      padding: tokens.spacing[3],
      backgroundColor: 'rgba(59, 130, 246, 0.08)',
      border: '1px solid rgba(59, 130, 246, 0.25)',
      borderRadius: tokens.borderRadius.md,
      fontSize: tokens.typography.fontSize.xs[0],
      color: 'var(--color-text-muted)',
    },
    warnBox: {
      padding: tokens.spacing[3],
      backgroundColor: 'rgba(234, 179, 8, 0.08)',
      border: `1px solid ${tokens.colors.warning[500]}40`,
      borderRadius: tokens.borderRadius.md,
      fontSize: tokens.typography.fontSize.sm[0],
      color: 'var(--color-text-muted)',
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: tokens.spacing[2],
      paddingTop: tokens.spacing[4],
      borderTop: '1px solid var(--color-border)',
    },
    cancelBtn: {
      padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
      fontSize: tokens.typography.fontSize.sm[0],
      fontFamily: tokens.typography.fontFamily.sans.join(', '),
      color: 'var(--color-text-muted)',
      backgroundColor: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: tokens.borderRadius.md,
      cursor: 'pointer',
    },
    primaryBtn: {
      padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
      fontSize: tokens.typography.fontSize.sm[0],
      fontFamily: tokens.typography.fontFamily.sans.join(', '),
      color: '#fff',
      backgroundColor: tokens.colors.primary[500],
      border: 'none',
      borderRadius: tokens.borderRadius.md,
      cursor: 'pointer',
    },
    disabledBtn: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    backLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: tokens.typography.fontSize.sm[0],
      color: 'var(--color-text-muted)',
      background: 'none',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily.sans.join(', '),
      marginBottom: tokens.spacing[4],
    },
    featureCount: {
      fontSize: tokens.typography.fontSize.sm[0],
      color: 'var(--color-text-muted)',
    },
  };

  // ── Phase: 'choose'
  const renderChoose = () => {
    const neitherReady = !jiraReady && !trelloReady;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
        {neitherReady && (
          <div style={s.warnBox}>
            No integrations configured.{' '}
            <button
              onClick={() => navigate('/settings')}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: tokens.colors.primary[400],
                cursor: 'pointer',
                fontSize: 'inherit',
                fontFamily: 'inherit',
                textDecoration: 'underline',
              }}
            >
              Set up in Settings →
            </button>
          </div>
        )}

        <div style={s.grid2}>
          {/* Jira option */}
          <button
            onClick={() => jiraReady && setPhase('jira')}
            disabled={!jiraReady}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: tokens.spacing[2],
              padding: tokens.spacing[5],
              backgroundColor: 'var(--color-bg-secondary)',
              border: `2px solid ${jiraReady ? 'var(--color-border)' : 'var(--color-border)'}`,
              borderRadius: tokens.borderRadius.xl,
              cursor: jiraReady ? 'pointer' : 'not-allowed',
              opacity: jiraReady ? 1 : 0.4,
              transition: 'border-color 0.15s, background-color 0.15s',
              textAlign: 'center',
              fontFamily: tokens.typography.fontFamily.sans.join(', '),
            }}
            onMouseEnter={(e) => {
              if (jiraReady) {
                e.currentTarget.style.borderColor = tokens.colors.primary[500];
                e.currentTarget.style.backgroundColor = `${tokens.colors.primary[500]}08`;
              }
            }}
            onMouseLeave={(e) => {
              if (jiraReady) {
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
          >
            <span style={{ fontSize: '28px' }}>🔷</span>
            <span style={{ fontSize: tokens.typography.fontSize.base[0], fontWeight: 600, color: 'var(--color-text)' }}>
              Jira
            </span>
            <span style={{ fontSize: tokens.typography.fontSize.xs[0], color: 'var(--color-text-muted)' }}>
              {jiraReady ? 'Create project' : 'Configure in Settings'}
            </span>
          </button>

          {/* Trello option */}
          <button
            onClick={() => trelloReady && setPhase('trello')}
            disabled={!trelloReady}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: tokens.spacing[2],
              padding: tokens.spacing[5],
              backgroundColor: 'var(--color-bg-secondary)',
              border: '2px solid var(--color-border)',
              borderRadius: tokens.borderRadius.xl,
              cursor: trelloReady ? 'pointer' : 'not-allowed',
              opacity: trelloReady ? 1 : 0.4,
              transition: 'border-color 0.15s, background-color 0.15s',
              textAlign: 'center',
              fontFamily: tokens.typography.fontFamily.sans.join(', '),
            }}
            onMouseEnter={(e) => {
              if (trelloReady) {
                e.currentTarget.style.borderColor = tokens.colors.primary[500];
                e.currentTarget.style.backgroundColor = `${tokens.colors.primary[500]}08`;
              }
            }}
            onMouseLeave={(e) => {
              if (trelloReady) {
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
          >
            <span style={{ fontSize: '28px' }}>📋</span>
            <span style={{ fontSize: tokens.typography.fontSize.base[0], fontWeight: 600, color: 'var(--color-text)' }}>
              Trello
            </span>
            <span style={{ fontSize: tokens.typography.fontSize.xs[0], color: 'var(--color-text-muted)' }}>
              {trelloReady ? 'Create board' : 'Configure in Settings'}
            </span>
          </button>
        </div>

        {/* Plan summary */}
        <div style={{
          paddingTop: tokens.spacing[3],
          borderTop: '1px solid var(--color-border)',
          fontSize: tokens.typography.fontSize.xs[0],
          color: 'var(--color-text-muted)',
        }}>
          {planLoading ? (
            <span>Loading plan data...</span>
          ) : planData ? (
            <span>{features.length} feature{features.length !== 1 ? 's' : ''} · {storyCount} stor{storyCount !== 1 ? 'ies' : 'y'}</span>
          ) : (
            <span>Could not load plan data</span>
          )}
        </div>
      </div>
    );
  };

  // ── Phase: 'jira'
  const renderJira = () => {
    const canPush = !isExporting && features.length > 0 && jiraProjectName.trim() && jiraProjectKey.trim();

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
        <button style={s.backLink} onClick={() => setPhase('choose')}>
          ← Back
        </button>

        <div>
          <label style={s.label}>Project Name</label>
          <input
            type="text"
            value={jiraProjectName}
            onChange={(e) => {
              setJiraProjectName(e.target.value);
              setJiraProjectKey(deriveProjectKey(e.target.value));
            }}
            placeholder="My Plan"
            style={s.input}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2], marginTop: tokens.spacing[1] }}>
            <p style={{ ...s.hint, margin: 0 }}>Project key:</p>
            <input
              type="text"
              value={jiraProjectKey}
              onChange={(e) =>
                setJiraProjectKey(
                  e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10)
                )
              }
              maxLength={10}
              style={{
                ...s.input,
                padding: '1px 6px',
                fontSize: tokens.typography.fontSize.xs[0],
                width: 'auto',
                minWidth: 60,
                display: 'inline-block',
              }}
            />
          </div>
        </div>

        <div style={s.infoBox}>
          Using saved credentials &middot; {jiraInt.jiraUrl}
        </div>

        <p style={s.featureCount}>
          Will create {features.length} feature{features.length !== 1 ? 's' : ''} and {storyCount} stor{storyCount !== 1 ? 'ies' : 'y'}
        </p>

        <div style={s.footer}>
          <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
          <button
            style={{ ...s.primaryBtn, ...(!canPush ? s.disabledBtn : {}) }}
            onClick={handlePushToJira}
            disabled={!canPush}
          >
            Push to Jira →
          </button>
        </div>
      </div>
    );
  };

  // ── Phase: 'trello'
  const renderTrello = () => {
    const canPush = !isExporting && features.length > 0 && trelloBoardName.trim();

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
        <button style={s.backLink} onClick={() => setPhase('choose')}>
          ← Back
        </button>

        <div>
          <label style={s.label}>Board Name</label>
          <input
            type="text"
            value={trelloBoardName}
            onChange={(e) => setTrelloBoardName(e.target.value)}
            placeholder="My Plan"
            style={s.input}
          />
        </div>

        <p style={s.featureCount}>
          Will create {features.length} list{features.length !== 1 ? 's' : ''} and {storyCount} card{storyCount !== 1 ? 's' : ''}
        </p>

        <div style={s.footer}>
          <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
          <button
            style={{ ...s.primaryBtn, ...(!canPush ? s.disabledBtn : {}) }}
            onClick={handlePushToTrello}
            disabled={!canPush}
          >
            Push to Trello →
          </button>
        </div>
      </div>
    );
  };

  const titleMap = {
    choose: 'Push to...',
    jira: 'Push to Jira',
    trello: 'Push to Trello',
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={titleMap[phase] || 'Push to...'}
        size="sm"
      >
        {phase === 'choose' && renderChoose()}
        {phase === 'jira' && renderJira()}
        {phase === 'trello' && renderTrello()}
      </Modal>

      <JiraExportProgress
        isOpen={showJiraProgress}
        onClose={() => {
          setShowJiraProgress(false);
          setJiraExportId(null);
        }}
        exportId={jiraExportId}
        onSuccess={(result) => {
          // Notify parent (e.g. to show the sync badge) but keep the
          // progress modal open so the user can review results first.
          onSuccess?.('jira', result);
        }}
        onError={() => {
          setShowJiraProgress(false);
          setJiraExportId(null);
        }}
      />

      <TrelloExportProgress
        isOpen={showTrelloProgress}
        onClose={() => {
          setShowTrelloProgress(false);
          setTrelloExportId(null);
        }}
        exportId={trelloExportId}
        onSuccess={(result) => {
          onSuccess?.('trello', result);
        }}
        onError={() => {
          setShowTrelloProgress(false);
          setTrelloExportId(null);
        }}
      />
    </>
  );
};

export default RoadmapPushModal;
